// SPDX-License-Identifier: AGPL-3.0-or-later

import {findUrlEnd, isUrlStart} from '@app/features/messaging/utils/markdown/UrlSpanUtils';

const ANGLE_BRACKET_TOKEN_PREFIXES = ['<:', '<a:', '<id:', '<@', '<#', '</', '<t:', '<+', '<sms:'];
const EVERYONE_MENTION = '@everyone';
const HERE_MENTION = '@here';
const SOFT_C_FOLLOWERS = new Set(['e', 'i', 'y']);
const ASCII_LETTER = /[A-Za-z]/;

const LETTER_MAP: Record<string, string> = {
	a: 'A',
	b: 'B',
	d: 'D',
	e: 'e',
	f: 'F',
	g: 'G',
	h: 'H',
	i: 'i',
	j: 'J',
	k: 'K',
	l: 'L',
	m: 'M',
	n: 'N',
	o: 'o',
	p: 'P',
	q: 'Q',
	r: 'R',
	s: 'S',
	t: 'T',
	u: 'U',
	v: 'v',
	w: 'W',
	x: 'X',
	y: 'y',
	z: 'Z',
};

const STEM_SUFFIXES = ['ness', 'ment', 'ful', 'ing', 'ers', 'est', 'ies', 'ed', 'es', 'er', 'ly', 'y', 's'] as const;

const SILENT_KH_WORDS: ReadonlySet<string> = new Set([
	'khaki',
	'khakis',
	'khan',
	'khans',
	'khmer',
	'sheikh',
	'sheikhs',
	'sheik',
	'sheiks',
	'sikh',
	'sikhs',
	'gurkha',
	'gurkhas',
]);

/** who/whom/whose/whole: keep WH. The W is not a throwaway silent letter. */
const WH_KEEP_BOTH_STEMS: ReadonlySet<string> = new Set([
	'who',
	'whom',
	'whose',
	'whole',
	'whoever',
	'whomever',
	'wholly',
	'wholesome',
	'whoop',
]);

const SILENT_H_INITIAL_STEMS: ReadonlySet<string> = new Set([
	'hour',
	'honest',
	'honesty',
	'honor',
	'honour',
	'honorary',
	'honorable',
	'honourable',
	'heir',
	'heiress',
	'heirloom',
	'herb',
	'herbal',
]);

const GH_PRONOUNCED_G_STEMS: ReadonlySet<string> = new Set(['spaghetti', 'aghast', 'yoghurt', 'yogurt', 'ghee']);

const GH_AS_F_STEMS: ReadonlySet<string> = new Set([
	'laugh',
	'laughter',
	'cough',
	'enough',
	'rough',
	'tough',
	'draught',
]);

const SILENT_L_STEMS: ReadonlySet<string> = new Set([
	'could',
	'would',
	'should',
	'walk',
	'talk',
	'chalk',
	'folk',
	'yolk',
	'half',
	'calf',
	'behalf',
	'calm',
	'palm',
	'balm',
	'psalm',
	'almond',
	'salmon',
]);

const SILENT_B_MB_STEMS: ReadonlySet<string> = new Set([
	'lamb',
	'comb',
	'bomb',
	'thumb',
	'climb',
	'crumb',
	'dumb',
	'numb',
	'womb',
	'tomb',
	'plumb',
	'plumber',
	'succumb',
	'jamb',
	'aplomb',
]);

const SILENT_B_BT_STEMS: ReadonlySet<string> = new Set(['debt', 'doubt', 'subtle']);

const SILENT_N_STEMS: ReadonlySet<string> = new Set(['hymn', 'column', 'autumn', 'damn', 'solemn', 'condemn']);

const SILENT_T_STEMS: ReadonlySet<string> = new Set([
	'castle',
	'listen',
	'often',
	'whistle',
	'christmas',
	'mortgage',
	'ballet',
	'gourmet',
	'rapport',
	'debut',
	'fasten',
	'hasten',
	'moisten',
	'soften',
	'wrestle',
	'nestle',
	'thistle',
	'bristle',
	'apostle',
	'glisten',
	'christen',
]);

const SILENT_S_STEMS: ReadonlySet<string> = new Set(['island', 'isle', 'aisle', 'debris', 'viscount']);

const SILENT_W_STEMS: ReadonlySet<string> = new Set(['two', 'answer', 'sword']);

const SILENT_G_IGN_STEMS: ReadonlySet<string> = new Set([
	'sign',
	'design',
	'align',
	'assign',
	'resign',
	'malign',
	'benign',
	'campaign',
	'champagne',
	'cologne',
	'diaphragm',
]);

const SILENT_G_EIGN_STEMS: ReadonlySet<string> = new Set(['foreign', 'reign', 'feign', 'deign', 'sovereign']);

const SILENT_U_AFTER_G_STEMS: ReadonlySet<string> = new Set([
	'guess',
	'guest',
	'guitar',
	'guard',
	'guide',
	'guild',
	'guilt',
	'guise',
	'guarantee',
]);

const SILENT_U_BUILD_STEMS: ReadonlySet<string> = new Set(['build', 'built', 'biscuit']);

const SILENT_C_STEMS: ReadonlySet<string> = new Set(['muscle']);

function countBackticks(content: string, start: number): number {
	let length = 0;
	while (content[start + length] === '`') {
		length++;
	}
	return length;
}

function findClosingBackticks(content: string, start: number, count: number): number | null {
	let index = start;
	while (index < content.length) {
		if (content[index] !== '`') {
			index++;
			continue;
		}
		const length = countBackticks(content, index);
		if (length === count) {
			return index + length;
		}
		index += length;
	}
	return null;
}

function findAngleTokenEnd(content: string, index: number): number | null {
	if (content[index] !== '<') {
		return null;
	}
	const hasPrefix = ANGLE_BRACKET_TOKEN_PREFIXES.some((prefix) => content.startsWith(prefix, index));
	if (!hasPrefix) {
		return null;
	}
	const close = content.indexOf('>', index + 1);
	if (close < 0) {
		return null;
	}
	return close + 1;
}

function findMentionKeywordEnd(content: string, index: number): number | null {
	if (content[index] !== '@') {
		return null;
	}
	const rest = content.slice(index).toLowerCase();
	for (const keyword of [EVERYONE_MENTION, HERE_MENTION]) {
		if (!rest.startsWith(keyword)) {
			continue;
		}
		const after = content[index + keyword.length] ?? '';
		if (after === '' || !/[A-Za-z0-9]/u.test(after)) {
			return index + keyword.length;
		}
	}
	return null;
}

function findProtectedSpanEnd(content: string, index: number): number | null {
	const backtickCount = countBackticks(content, index);
	if (backtickCount > 0) {
		return findClosingBackticks(content, index + backtickCount, backtickCount);
	}

	const mentionEnd = findMentionKeywordEnd(content, index);
	if (mentionEnd != null) {
		return mentionEnd;
	}

	const angleEnd = findAngleTokenEnd(content, index);
	if (angleEnd != null) {
		return angleEnd;
	}

	if (!isUrlStart(content, index)) {
		return null;
	}
	const urlEnd = findUrlEnd(content, index);
	return urlEnd > index ? urlEnd : null;
}

function readAsciiLetterRunEnd(content: string, start: number): number {
	let end = start;
	while (end < content.length && ASCII_LETTER.test(content[end] ?? '')) {
		end++;
	}
	return end;
}

function matchesListedStem(word: string, stems: ReadonlySet<string>): boolean {
	if (stems.has(word)) {
		return true;
	}
	for (const suffix of STEM_SUFFIXES) {
		if (word.length > suffix.length + 1 && word.endsWith(suffix) && stems.has(word.slice(0, -suffix.length))) {
			return true;
		}
	}
	return false;
}

function applyOnStem(word: string, stems: ReadonlySet<string>, transform: (stem: string) => string): string {
	if (stems.has(word)) {
		return transform(word);
	}
	for (const suffix of STEM_SUFFIXES) {
		if (word.length <= suffix.length + 1 || !word.endsWith(suffix)) {
			continue;
		}
		const stem = word.slice(0, -suffix.length);
		if (stems.has(stem)) {
			return transform(stem) + word.slice(stem.length);
		}
	}
	return word;
}

function deleteFirst(word: string, letter: string): string {
	const index = word.indexOf(letter);
	if (index < 0) {
		return word;
	}
	return word.slice(0, index) + word.slice(index + 1);
}

function stripInitialClusters(word: string): string {
	if (word.startsWith('kn') || word.startsWith('gn') || word.startsWith('wr')) {
		return word.slice(1);
	}
	if (word.startsWith('ps') || word.startsWith('pn') || word.startsWith('pt')) {
		return word.slice(1);
	}
	if (word.startsWith('rh') || word.startsWith('gh')) {
		return (word[0] ?? '') + word.slice(2);
	}
	if (word.startsWith('wh') && !matchesListedStem(word, WH_KEEP_BOTH_STEMS)) {
		return `w${word.slice(2)}`;
	}
	return applyOnStem(word, SILENT_H_INITIAL_STEMS, (stem) => stem.slice(1));
}

function stripGh(word: string): string {
	let next = word.replaceAll('eigh', 'ei').replaceAll('igh', 'i');
	next = applyOnStem(next, GH_AS_F_STEMS, (stem) => stem.replaceAll('gh', 'f'));
	next = applyOnStem(next, GH_PRONOUNCED_G_STEMS, (stem) => stem.replaceAll('gh', 'g'));
	next = next.replaceAll('ough', 'ou').replaceAll('augh', 'au');
	return next;
}

/** Kept for later: extra silent-letter drops are still being reviewed. Live convertToAlphabet does not call this unless stripExtraSilentLetters is set. */
function stripSilentLetters(word: string): string {
	let next = SILENT_KH_WORDS.has(word) ? word.replaceAll('kh', 'k') : word;
	next = applyOnStem(next, SILENT_C_STEMS, (stem) => deleteFirst(stem, 'c'));
	next = applyOnStem(next, SILENT_L_STEMS, (stem) => deleteFirst(stem, 'l'));
	next = applyOnStem(next, SILENT_B_MB_STEMS, (stem) => stem.replace('mb', 'm'));
	next = applyOnStem(next, SILENT_B_BT_STEMS, (stem) => stem.replace('bt', 't'));
	next = applyOnStem(next, SILENT_N_STEMS, (stem) => stem.replace('mn', 'm'));
	next = applyOnStem(next, SILENT_T_STEMS, (stem) => deleteFirst(stem, 't'));
	next = applyOnStem(next, SILENT_S_STEMS, (stem) => deleteFirst(stem, 's'));
	next = applyOnStem(next, SILENT_W_STEMS, (stem) => deleteFirst(stem, 'w'));
	next = applyOnStem(next, SILENT_G_IGN_STEMS, (stem) =>
		stem.replace('ign', 'in').replace('gn', 'n').replace('gm', 'm'),
	);
	next = applyOnStem(next, SILENT_G_EIGN_STEMS, (stem) => stem.replace('eign', 'ein'));
	next = applyOnStem(next, SILENT_U_AFTER_G_STEMS, (stem) => stem.replace('gu', 'g'));
	next = applyOnStem(next, SILENT_U_BUILD_STEMS, (stem) => deleteFirst(stem, 'u'));
	if (next.endsWith('gue') && next.length > 3) {
		next = next.slice(0, -2);
	}
	next = stripInitialClusters(next);
	next = stripGh(next);
	next = next.replace(/sc([eiy])/g, 's$1');
	return next;
}

function convertLetters(text: string): string {
	const pieces: Array<string> = [];
	let index = 0;
	while (index < text.length) {
		const char = text[index] ?? '';
		const lower = char.toLowerCase();
		if (lower === 'c') {
			const next = (text[index + 1] ?? '').toLowerCase();
			if (next === 'k') {
				pieces.push('K');
				index += 2;
				continue;
			}
			if (SOFT_C_FOLLOWERS.has(next)) {
				pieces.push('S');
				index++;
				continue;
			}
			pieces.push('K');
			index++;
			continue;
		}
		pieces.push(LETTER_MAP[lower] ?? char);
		index++;
	}
	return pieces.join('');
}

function convertWord(word: string, stripExtraSilentLetters: boolean): string {
	const lower = word.toLowerCase();
	if (stripExtraSilentLetters) {
		return convertLetters(stripSilentLetters(lower));
	}
	const withKhaki = SILENT_KH_WORDS.has(lower) ? lower.replaceAll('kh', 'k') : lower;
	return convertLetters(withKhaki);
}

export interface ConvertToAlphabetOptions {
	/** Extra silent-letter drops (ghost, knife, …). Off for live display/send until those rules are reviewed. */
	stripExtraSilentLetters?: boolean;
}

export function convertToAlphabet(content: string, options?: ConvertToAlphabetOptions): string {
	if (content.length === 0) {
		return content;
	}

	const stripExtraSilentLetters = options?.stripExtraSilentLetters === true;
	const pieces: Array<string> = [];
	let index = 0;
	while (index < content.length) {
		const protectedEnd = findProtectedSpanEnd(content, index);
		if (protectedEnd != null && protectedEnd > index) {
			pieces.push(content.slice(index, protectedEnd));
			index = protectedEnd;
			continue;
		}

		const wordEnd = readAsciiLetterRunEnd(content, index);
		if (wordEnd > index) {
			pieces.push(convertWord(content.slice(index, wordEnd), stripExtraSilentLetters));
			index = wordEnd;
			continue;
		}

		pieces.push(content[index] ?? '');
		index++;
	}

	return pieces.join('');
}

export function convertAlphabetDisplayContent(
	content: string,
	enabled: boolean,
	messageId: string | undefined,
): string {
	if (!enabled || messageId == null || messageId === '') {
		return content;
	}
	return convertToAlphabet(content);
}
